import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useHaptics } from "@/hooks/useHaptics";
import { CheckInReason } from "./CheckInOptionsDialog";

interface SelfieCheckInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventTitle: string;
  reason?: CheckInReason;
  distance?: number;
}

const getReasonMessage = (reason?: CheckInReason, distance?: number): { title: string; description: string } => {
  switch (reason) {
    case "too_far":
      return {
        title: "Check In with Selfie",
        description: distance 
          ? `You're ${distance.toFixed(1)} miles from the event location. Submit a selfie so we can verify you attended.`
          : "You're outside the event location. Submit a selfie so we can verify you attended."
      };
    case "location_denied":
      return {
        title: "Check In with Selfie",
        description: "Since location services are unavailable, we need a selfie to verify you're at the event."
      };
    case "event_ended":
      return {
        title: "Submit Late Check-In",
        description: "This event has ended. Submit a selfie for manual review by our team within 7 days."
      };
    case "event_not_started":
      return {
        title: "Early Check-In",
        description: "Check-in opens 1 hour before the event. Submit your selfie now and we'll verify it when the window opens."
      };
    case "no_location_data":
      return {
        title: "Check In with Selfie",
        description: "We couldn't determine your location. Submit a selfie to verify your attendance."
      };
    case "location_loading":
      return {
        title: "Getting Your Location",
        description: "Please wait while we determine your position..."
      };
    default:
      return {
        title: "Check In with Selfie",
        description: "Take or upload a selfie from the event to verify your attendance. Your photo will be reviewed by an admin."
      };
  }
};

export const SelfieCheckInDialog = ({
  open,
  onOpenChange,
  eventId,
  eventTitle,
  reason,
  distance,
}: SelfieCheckInDialogProps) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success: hapticSuccess } = useHaptics();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const submitSelfieMutation = useMutation({
    mutationKey: ["selfie-check-in"],
    mutationFn: async () => {
      if (!selectedFile) throw new Error("No file selected");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upload to storage
      const fileExt = selectedFile.name.split(".").pop();
      const fileName = `${user.id}/${eventId}_${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("private-user-media")
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Call edge function to verify
      const { data, error } = await supabase.functions.invoke(
        "verify-selfie-attendance",
        {
          body: {
            eventId,
            photoPath: fileName,
            caption: `Check-in selfie for ${eventTitle}`,
          },
        }
      );

      if (error) throw error;
      if (!data.success) throw new Error(data.reason || "Failed to submit selfie");

      return data;
    },
    onSuccess: (data) => {
      hapticSuccess();
      toast.success("Selfie submitted!", {
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ["today-checkin-events"] });
      queryClient.invalidateQueries({ queryKey: ["event-attendance"] });
      onOpenChange(false);
      resetDialog();
    },
    onError: (error: Error) => {
      toast.error("Failed to submit selfie", {
        description: error.message,
      });
    },
  });

  const resetDialog = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const reasonMessage = getReasonMessage(reason, distance);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{reasonMessage.title}</DialogTitle>
          <DialogDescription>
            {reasonMessage.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {previewUrl ? (
            <div className="relative">
              <img
                src={previewUrl}
                alt="Selfie preview"
                className="w-full aspect-square object-cover rounded-lg"
              />
              <Button aria-label="Remove photo"
                size="icon"
                variant="destructive"
                className="absolute top-2 right-2"
                onClick={resetDialog}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Select a photo from your device
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose Photo
              </Button>
            </div>
          )}


          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetDialog();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => submitSelfieMutation.mutate()}
              disabled={!selectedFile || submitSelfieMutation.isPending}
              className="flex-1"
            >
              {submitSelfieMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Check-In"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
